/**
 * PlatformerCamera — smooth follow + zoom camera for the platformer.
 *
 * It owns NO Pixi nodes. Each frame the scene tells it where the player
 * is (and, optionally, a scripted "override" focus/zoom from a camera
 * zone), the camera eases its internal state toward those targets, and
 * `apply(container)` writes the resulting scale + position onto the world
 * container. The returned `{ camX, camY }` (the camera's top-left in WORLD
 * units) is fed straight into the board's existing parallax so backgrounds
 * keep scrolling correctly at any zoom.
 *
 * Transform model — a world point `W` lands on screen at:
 *     screen = (W − camTopLeft) · zoom
 * so to centre a focus point `F`:
 *     camTopLeft = F − view/(2·zoom)
 * Everything (tiles, entities, backgrounds, particles) lives under the
 * world container, so a single `container.scale = zoom` zooms it all
 * uniformly — no per-system zoom plumbing.
 *
 * Zoom semantics: zoom > 1 = zoomed IN (closer, smaller view); zoom < 1 =
 * zoomed OUT (wider view). The view is clamped to the world bounds so the
 * camera never reveals past an edge; when the world is smaller than the
 * view on an axis it is centred (X) or bottom-anchored (Y, keeping the
 * ground low like the classic platformer framing).
 *
 * Easing is frame-rate independent (exponential approach by `1 − e^(−k·dt)`)
 * so the feel is identical at 30, 60 or 144 FPS. `snap()` jumps straight to
 * the targets (used on stage mount / respawn so the camera doesn't visibly
 * glide in from the previous run's position).
 */
export class PlatformerCamera {
    constructor() {
        this.viewW = 1920; this.viewH = 1080;
        this.worldW = 1920; this.worldH = 1080;

        // Base follow zoom (the stage default) and the live/target zoom.
        this.baseZoom = 1;
        this.zoom = 1;
        this._targetZoom = 1;

        // Focus point (world coords) the camera centres on — the player by
        // default, or a scripted point while an override is active.
        this.focusX = 0; this.focusY = 0;
        this._targetFocusX = 0; this._targetFocusY = 0;

        // Scripted override (a camera zone). null = plain follow.
        //   { zoom, focusX|null, focusY|null }  (null axis = keep following
        //   the player on that axis).
        this._override = null;

        // Easing rates (1/sec). Higher = snappier.
        this.followSmooth = 8;
        this.zoomSmooth = 5;

        // Hard limits so a bad zone value can't break framing.
        this.minZoom = 0.25;
        this.maxZoom = 4;

        // ── Screen shake (trauma model, Vlambeer-style) ──────────
        // Events ADD trauma (0..1); shake amplitude is trauma² (so small
        // hits barely wobble and big hits rattle), decaying linearly.
        // The offset is layered noise (detuned sines) — cheap, smooth,
        // and never resonates visibly.
        this._trauma = 0;
        this._shakeTime = 0;
        /** Max shake displacement at full trauma, in screen px. */
        this.maxShakePx = 22;
        /** Trauma drained per second. */
        this.traumaDecay = 1.6;
    }

    /** Add screen-shake trauma (0..1); stacks and clamps at 1. */
    addTrauma(amount) {
        this._trauma = Math.min(1, this._trauma + Math.max(0, amount || 0));
    }

    /**
     * (Re)configure for a stage. `baseZoom` is the default follow zoom.
     * Snaps state to the new base so there's no glide on load.
     */
    configure({ viewW, viewH, worldW, worldH, baseZoom = 1, followSmooth, zoomSmooth } = {}) {
        if (viewW > 0) this.viewW = viewW;
        if (viewH > 0) this.viewH = viewH;
        if (worldW > 0) this.worldW = worldW;
        if (worldH > 0) this.worldH = worldH;
        this.baseZoom = clamp(baseZoom || 1, this.minZoom, this.maxZoom);
        if (Number.isFinite(followSmooth)) this.followSmooth = followSmooth;
        if (Number.isFinite(zoomSmooth)) this.zoomSmooth = zoomSmooth;
        this.zoom = this._targetZoom = this.baseZoom;
        this._override = null;
    }

    /** Set the scripted override (from a camera zone). */
    setOverride(zoom, focusX = null, focusY = null) {
        const z = clamp(zoom || this.baseZoom, this.minZoom, this.maxZoom);
        this._override = { zoom: z, focusX, focusY };
    }

    /** Drop the override → ease back to following the player at base zoom. */
    clearOverride() { this._override = null; }

    hasOverride() { return this._override !== null; }

    /**
     * Advance the camera. `dt` in seconds; (`px`,`py`) = the player CENTRE
     * in world coords (the default focus).
     */
    update(dt, px, py) {
        const ov = this._override;
        this._targetZoom = ov ? ov.zoom : this.baseZoom;
        this._targetFocusX = (ov && ov.focusX != null) ? ov.focusX : px;
        this._targetFocusY = (ov && ov.focusY != null) ? ov.focusY : py;

        const kz = 1 - Math.exp(-this.zoomSmooth * Math.max(0, dt));
        const kf = 1 - Math.exp(-this.followSmooth * Math.max(0, dt));
        this.zoom += (this._targetZoom - this.zoom) * kz;
        this.focusX += (this._targetFocusX - this.focusX) * kf;
        this.focusY += (this._targetFocusY - this.focusY) * kf;

        // Drain shake trauma + advance the noise clock.
        if (this._trauma > 0) {
            this._trauma = Math.max(0, this._trauma - this.traumaDecay * Math.max(0, dt));
            this._shakeTime += dt;
        }
    }

    /** Jump straight to the targets (no easing) — mount / respawn. */
    snap(px, py) {
        const ov = this._override;
        this.zoom = ov ? ov.zoom : this.baseZoom;
        this.focusX = (ov && ov.focusX != null) ? ov.focusX : px;
        this.focusY = (ov && ov.focusY != null) ? ov.focusY : py;
    }

    /**
     * Write the current transform onto the world container and return the
     * camera's world-space top-left `{ camX, camY }` for parallax.
     */
    apply(container) {
        const z = this.zoom;
        const vw = this.viewW / z;          // visible world width
        const vh = this.viewH / z;          // visible world height
        const camX = clampAxis(this.focusX, this.worldW, vw, 'center');
        const camY = clampAxis(this.focusY, this.worldH, vh, 'bottom');
        // Shake is applied as a SCREEN-space pixel offset on top of the
        // clamped camera transform (so it still rattles at world edges)
        // while the returned camX/camY stay clean for the parallax.
        let shakeX = 0, shakeY = 0;
        if (this._trauma > 0) {
            const amp = this._trauma * this._trauma * this.maxShakePx;
            const t = this._shakeTime;
            // Two detuned sines per axis ≈ smooth pseudo-noise.
            shakeX = amp * Math.sin(t * 39.7) * Math.sin(t * 13.1 + 1.7);
            shakeY = amp * Math.sin(t * 47.3 + 0.9) * Math.sin(t * 11.3);
        }
        container.scale.set(z);
        container.x = Math.round(-camX * z + shakeX);
        container.y = Math.round(-camY * z + shakeY);
        return { camX, camY };
    }
}

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

/**
 * Clamp a camera axis so the visible window stays inside the world. When
 * the world is SMALLER than the view on this axis we can't fill it, so we
 * either centre it ('center', good for X) or anchor it so the world's far
 * edge sits at the screen's far edge ('bottom', keeps the ground low on Y).
 */
function clampAxis(focus, world, view, mode) {
    if (world <= view) {
        return mode === 'bottom' ? (world - view) : (world - view) / 2;
    }
    const c = focus - view / 2;
    if (c < 0) return 0;
    const max = world - view;
    return c > max ? max : c;
}
